// POI-Modul — Points of Interest mit Server-Backed-Blob-Cache.
// ------------------------------------------------------------
// Architektur-Update (Batch 8 nach iPad-"server lost"-Drama):
//
// Vorher: Client fetchte direkt gegen Overpass (3-Mirror-Fallback) und
// schrieb N Rows (500-2000 Trinkwasser-Punkte) in IDB `pois`-Store. iOS
// Safari killte bei so großen Multi-Put-Transaktionen regelmäßig die
// IDB-Connection oder den ganzen IDB-Server-Prozess ("Connection to
// indexed Database server lost. Refresh the page to try again.").
//
// Jetzt: der Backend-Server (backend/routes/pois.js) hält den Overpass-
// Cache zentral vor und liefert pro Kategorie einen einzigen JSON-Blob
// (gzipped, ~80-150 KB). Der Client fetchtmed ihn und legt ihn als EINE
// IDB-Row in `poiBlobs` ab — ein put(), keine Multi-Row-TX mehr. iOS
// Safari ist damit happy, zusätzlich sparen wir uns im Client:
//   - Overpass-Mirror-Fallback-Logik
//   - Timeout-Handling für langsame Mirrors
//   - 429/503-Retry
//   - Bounding-Box-Hardcoding
//
// Exportierte API bleibt weitgehend identisch (loadCachedPois,
// clearPoiCache, forceRefreshPoiCategory, ...), damit Callsites in
// MapPage/OfflinePage/POIButton unverändert bleiben.

import { withDb } from './storage/db';

// Overpass-gestützte POI-Kategorien (müssen 1:1 zu backend/routes/pois.js
// POI_CATEGORIES passen — der Server validiert strikt). drinking_water wird
// in MapPage speziell gerendert (gemischt mit admin/user-POIs); die übrigen
// laufen über den generischen Overpass-Layer (lib/map/overpassPoiLayer.ts).
export type PoiCategoryId =
  | 'drinking_water'
  | 'fast_food'
  | 'restaurant_indian'
  | 'camp_site'
  | 'bakery'
  | 'supermarket'
  | 'bicycle_shop'
  | 'shelter'
  | 'swimming'
  | 'decathlon'
  | 'fuel_shop';

/** Die Overpass-Kategorien außer drinking_water — generisch gerendert/filterbar. */
export const OVERPASS_EXTRA_CATEGORIES: PoiCategoryId[] = [
  'fast_food',
  'restaurant_indian',
  'camp_site',
  'bakery',
  'supermarket',
  'bicycle_shop',
  'shelter',
  'swimming',
  'decathlon',
  'fuel_shop',
];

export interface POI {
  id: string;              // `${category}:${osmId}` (OSM) bzw. `google:<placeId>`
  category: PoiCategoryId;
  lat: number;
  lon: number;
  name?: string | null;
  osmId: number;
  tags?: Record<string, string>;
  cachedAt: string;        // ISO
  source?: 'osm' | 'google'; // Herkunft; live-Google-Treffer sind 'google'.
}

/**
 * Kategorien, für die Live-Google-Places genauer ist als OSM (Eigentümer
 * pflegen Google, kaum jemand OSM). Der Pit-Stop-Finder fragt diese live ab
 * wenn online; sonst OSM-Cache. Muss zu GOOGLE_CONFIG in backend/routes/pois.js
 * passen.
 */
export const GOOGLE_LIVE_CATEGORIES: ReadonlySet<PoiCategoryId> = new Set([
  'fast_food',
  'restaurant_indian',
  'bakery',
  'supermarket',
  // ULTRA: zusätzlich live von Google für verlässliche Öffnungszeiten
  // (OSM hatte sie quasi nie). Muss zu GOOGLE_CONFIG in
  // backend/routes/pois.js passen.
  'decathlon',
  'bicycle_shop',
  'fuel_shop',
  'swimming',
]);

/** Offen-Status eines POI für die UI. */
export type OpenState = 'open' | 'closed' | 'unknown';

// Ein `open_now`-Flag (von Google) gilt nur für den Moment des API-Calls.
// Aus dem persistenten Cache (bis 60 Tage) wäre es längst veraltet — daher
// vertrauen wir ihm nur, wenn die Daten frisch sind. Danach → 'unknown'
// (das Detail-Popup zeigt dann den Wochenplan, der Fahrer entscheidet selbst).
const OPEN_NOW_TRUST_MS = 2 * 60 * 60 * 1000; // 2 h

/**
 * Offen/geschlossen/unbekannt für einen POI. Basiert auf dem `open_now`-Tag
 * (von Google live mitgeliefert). Veraltete (gecachte) Flags → 'unknown'.
 */
export function poiOpenState(poi: POI): OpenState {
  const on = poi.tags?.open_now;
  if (on !== 'true' && on !== 'false') return 'unknown';
  const ts = Date.parse(poi.cachedAt);
  if (Number.isFinite(ts) && Date.now() - ts > OPEN_NOW_TRUST_MS) return 'unknown';
  return on === 'true' ? 'open' : 'closed';
}

/** Lesbarer Wochenplan (Google `weekdayDescriptions`), oder null. */
export function poiOpeningHoursText(poi: POI): string | null {
  return poi.tags?.opening_hours || null;
}

export interface PoiCategory {
  id: PoiCategoryId;
  label: string;           // UI-Label ("Drinking water")
  emoji: string;           // Marker-Icon
  color?: string;          // Pin-Kreis-Farbe (generischer Overpass-Layer)
  hint?: string;           // kurzer Erklär-Text fürs Filter-Panel
}

// Kategorien-Registry (Client-Side). Bleibt parallel zur Server-Registry
// in backend/routes/pois.js. Beim Hinzufügen neuer Kategorien müssen
// beide Seiten aktualisiert werden — die Server-Route validiert strikt
// gegen ihre eigene Liste, so dass unbekannte Kategorien früh 404en.
export const POI_CATEGORIES: Record<PoiCategoryId, PoiCategory> = {
  drinking_water: {
    id: 'drinking_water',
    label: 'Drinking water',
    emoji: '💧',
    color: 'rgba(14,165,233,0.95)',
    hint: 'Fountains, taps and springs (OSM + admin + user).',
  },
  fast_food: {
    id: 'fast_food',
    label: 'Fast food / kebab',
    emoji: '🥙',
    color: 'rgba(234,88,12,0.95)',
    hint: 'Kebab, burgers, snack bars — quick calories.',
  },
  restaurant_indian: {
    id: 'restaurant_indian',
    label: 'Indian restaurant',
    emoji: '🍛',
    color: 'rgba(217,119,6,0.95)',
    hint: 'Restaurants tagged cuisine=indian.',
  },
  camp_site: {
    id: 'camp_site',
    label: 'Campsite',
    emoji: '⛺',
    color: 'rgba(22,163,74,0.95)',
    hint: 'Official campsites to sleep.',
  },
  bakery: {
    id: 'bakery',
    label: 'Bakery',
    emoji: '🥖',
    color: 'rgba(202,138,4,0.95)',
    hint: 'Bakeries — early-morning fuel.',
  },
  supermarket: {
    id: 'supermarket',
    label: 'Supermarket',
    emoji: '🛒',
    color: 'rgba(5,150,105,0.95)',
    hint: 'Supermarkets for resupply.',
  },
  bicycle_shop: {
    id: 'bicycle_shop',
    label: 'Bike shop',
    emoji: '🚲',
    color: 'rgba(37,99,235,0.95)',
    hint: 'Bike shops — repair, parts, live opening hours from Google.',
  },
  shelter: {
    id: 'shelter',
    label: 'Shelter / hut',
    emoji: '🛖',
    color: 'rgba(120,113,108,0.95)',
    hint: 'Mountain huts, wilderness huts, shelters to sleep.',
  },
  swimming: {
    id: 'swimming',
    label: 'Pool / swimming',
    emoji: '🏊',
    color: 'rgba(8,145,178,0.95)',
    hint: 'Public pools and baths to cool off.',
  },
  decathlon: {
    id: 'decathlon',
    label: 'Decathlon',
    emoji: '🏬',
    color: 'rgba(30,64,175,0.95)',
    hint: 'Decathlon stores — bike gear & spares.',
  },
  fuel_shop: {
    id: 'fuel_shop',
    label: 'Tankstelle (Kiosk)',
    emoji: '⛽',
    color: 'rgba(120,113,108,0.95)',
    hint: 'Tankstellen mit Shop/Kiosk — Notfall-Essen, v.a. sonntags.',
  },
};

// Wir re-fetchen einen Kategorie-Blob nach 14d staleness. Der Server
// refreshed intern ebenfalls ~alle 14d aus Overpass + 12h-Hintergrund-
// Tasks, also selbst wenn der Client nie explizit refresht, kriegt er
// beim nächsten GET immer relativ frische Daten.
const CACHE_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

// Daten-Version des POI-Blobs. Hochzählen, wenn sich der KORRIDOR ändert (neue
// Region/Strecke → neue OSM-Abdeckung), damit Clients NICHT bis zu 14 Tage auf
// den alten Blob warten, sondern sofort neu laden. v2 = "zur eurobike"-Korridor
// dazugekommen (Freiburg→Frankfurt). v3 = der italienische Renn-Korridor
// (Gorgonzola→Castelletto→Vercelli, Piemont/Lombardei) ist in die Korridor-
// Kacheln gekommen, OHNE dass v2 mitgezogen wurde — Clients mit einem v2-Blob
// aus der Zeit davor servierten weiter die alte Liguria-Abdeckung und zeigten
// zwischen Mailand-West und Piemont KEINE Trinkwasser-POIs, obwohl der Server
// sie längst liefert. v3 erzwingt bei allen Clients einen Vordergrund-Refetch.
// v4 = der v3-Blob war ein Teil-Scan (complete:false) mit einem Loch über
// Verona/Gardasee (drinking_water-Chunk timeoutete, s. backend CHUNK-Fix 8→3).
// Clients hatten den lückenhaften v3-Blob gecacht und hätten ihn 14 Tage
// behalten; v4 zwingt sie, den korrigierten (Verona-vollständigen) Blob zu
// holen. Ein gespeicherter Blob mit abweichender Version gilt als veraltet.
// ULTRA-Hotfix (Rennen): v5 — Clients hatten den v4-Blob MIT dem Verona-Süd-
// Loch (USER_BBOX 0) gecacht und hielten ihn 14 Tage. Der Server-Blob ist
// jetzt gepatcht (42697 POIs, 349 entlang der Südroute, cachedAt 13:37:31Z);
// v5 zwingt ALLE Clients zum Komplett-Refetch, statt auf den lückenhaften
// v4-Cache zu matchen.
// ULTRA-Hotfix (Rennen, Ivrea→Barcelona): v6 — der Server-Background-Rescan
// hatte den drinking_water-Blob auf einen TEIL-SCAN (23.225 POIs,
// complete:false) geschrumpft → Coverage-Löcher in Alpen/Riviera/Spanien-Küste.
// Frischer Overpass-Fetch der 3 Gap-Regionen gemerged → 45.264 POIs,
// complete:true (Alps 6.375, Riviera 2.806, Barcelona 5.740). v6 zwingt ALLE
// Clients (die den 23k-Blob 14 Tage gecacht hätten) zum Komplett-Refetch.
// ULTRA-Hotfix (Aosta): v7 — fast_food & bicycle_shop waren als TEIL-SCAN
// (complete:false) gecacht: die dichte Aosta-Kachel (7.0–7.5 / 45.5–46.0)
// sprengte den Overpass-Timeout und riss den ganzen 3-Kachel-Chunk mit → in
// Aosta KEINE Fastfood-/Fahrradladen-POIs (drinking_water war complete, daher
// ok). Backend-Fix: fehlgeschlagene Chunks werden jetzt Kachel-für-Kachel
// nachgezogen (fetchFromOverpass) → Scans laufen complete durch. v7 zwingt
// ALLE Clients, die den lückenhaften v6-Blob 14 Tage gecacht hätten, zum
// Komplett-Refetch der jetzt vollständigen Server-Blobs.
// v8 — Umstellung auf BUILD-ZEIT-Extrakte (Abschnitt 1 corridor-selfservice-
// spec): der Server serviert die POIs jetzt deterministisch aus dem OSM-PBF
// (scripts/extract-corridor-pois.mjs → data/pois-static/) statt aus Overpass-
// Kacheln. v8 zwingt alle Clients, die einen (potenziell löchrigen) Overpass-
// Blob bis zu 14 Tage gecacht hätten, zum Refetch der vollständigen Extrakte.
const POI_BLOB_VERSION = 8;

// Net-Timeout für den Backend-Fetch. 20s ist großzügig — falls der Server
// gerade selbst Overpass anwirft (Sync-Refresh beim ersten Request), kann
// es kurz dauern.
const POI_FETCH_TIMEOUT_MS = 20_000;

// Server-Stand pro Kategorie (`cachedAt` aus /api/pois/_status).
//
// POI_BLOB_VERSION oben ist eine HAND gepflegte Konstante — das reichte,
// solange neue Korridore nur per Deploy dazukamen. Mit den Self-Service-
// Korridoren ändert sich der POI-Bestand des Servers jederzeit, ohne dass
// ein Mensch etwas hochzählt: baut jemand einen Korridor, sind dort ab
// sofort Bäcker/Supermärkte/Camping da — der Client würde sie ohne diesen
// Check aber bis zu 14 Tage (CACHE_MAX_AGE_MS) nicht sehen, weil sein Blob
// formal "frisch" ist. Deshalb: ein `_status`-Abruf (wenige KB) pro App-
// Start, und genau die Kategorien nachladen, deren Server-`cachedAt` sich
// geändert hat. Gilt in beide Richtungen — läuft die Retention über einen
// Korridor, fällt `cachedAt` zurück und die POIs verschwinden wieder.
//
// Offline wird der Check komplett übersprungen: der gecachte Blob ist dann
// die einzige Wahrheit, und im Rennen darf nichts auf ein Timeout warten.
const POI_STATUS_TTL_MS = 5 * 60 * 1000;
const POI_STATUS_TIMEOUT_MS = 3_500;

type PoiStatusMap = Record<string, string>;
let poiStatusCache: { at: number; map: PoiStatusMap } | null = null;
let poiStatusInflight: Promise<PoiStatusMap | null> | null = null;

async function serverPoiStatus(): Promise<PoiStatusMap | null> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return null;
  if (poiStatusCache && Date.now() - poiStatusCache.at < POI_STATUS_TTL_MS) {
    return poiStatusCache.map;
  }
  if (poiStatusInflight) return poiStatusInflight;
  poiStatusInflight = (async (): Promise<PoiStatusMap | null> => {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), POI_STATUS_TIMEOUT_MS);
    try {
      const res = await fetch(`/api/pois/_status?_=${Date.now()}`, {
        signal: ctrl.signal,
        cache: 'reload',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      const data = (await res.json()) as {
        categories?: Record<string, { cachedAt?: string }>;
      };
      const map: PoiStatusMap = {};
      for (const [cat, entry] of Object.entries(data.categories ?? {})) {
        if (typeof entry?.cachedAt === 'string') map[cat] = entry.cachedAt;
      }
      poiStatusCache = { at: Date.now(), map };
      return map;
    } catch {
      // Offline / Server weg → fail-open, der Cache bleibt gültig.
      return null;
    } finally {
      clearTimeout(t);
      poiStatusInflight = null;
    }
  })();
  return poiStatusInflight;
}

/**
 * Hat der Server für diese Kategorie einen anderen Stand als unser Blob?
 * `false` bei Offline/Fehler/unbekannter Kategorie — nie ein Fehlalarm, der
 * einen brauchbaren Cache wegwirft.
 */
async function serverBlobDiffers(
  category: PoiCategoryId,
  cachedAt: string | undefined,
): Promise<boolean> {
  if (!cachedAt) return false;
  const map = await serverPoiStatus();
  const serverCachedAt = map?.[category];
  if (!serverCachedAt) return false;
  return serverCachedAt !== cachedAt;
}

/** Server-Response-Format (muss mit backend/routes/pois.js übereinstimmen). */
interface PoiBlobResponse {
  category: string;
  count: number;
  cachedAt: string;
  pois: POI[];
}

/** IDB-Row-Format für poiBlobs (gleich wie Server-Response). */
interface PoiBlobRow extends PoiBlobResponse {
  category: PoiCategoryId;
  /** Daten-Version (POI_BLOB_VERSION) zum Zeitpunkt des Speicherns. Fehlt bei
   *  Alt-Blobs (< v2) → gelten als veraltet → Refetch. */
  v?: number;
}

/**
 * Holt den POI-Blob einer Kategorie vom Backend. Der Server übernimmt
 * Overpass-Aufrufe + Mirror-Fallback + Cache — der Client spricht nur
 * noch mit einem Endpoint. Browser dekomprimiert gzip automatisch.
 */
async function fetchPoiBlobFromBackend(
  category: PoiCategoryId,
): Promise<PoiBlobResponse> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), POI_FETCH_TIMEOUT_MS);
  try {
    // Wave 14cf-fix6: Cache-Buster im Query gegen alle Layer (Service-
    // Worker, Browser-HTTP-Cache, Cloudflare-Edge). User berichteten dass
    // der App-Refresh nach Server-Multi-Region-Update weiter den alten
    // count zeigte — der Server-Origin lieferte frische Daten, aber
    // irgendein Cache zwischen Client und Origin held einen alten Stand.
    // Mit `?_=<timestamp>` ist die URL pro Refresh unique, kein Cache-Hit
    // mehr möglich. Plus `cache: 'reload'` damit der Browser zwingend
    // ans Netz geht.
    const url = `/api/pois/${encodeURIComponent(category)}?_=${Date.now()}`;
    const res = await fetch(url, {
      signal: ctrl.signal,
      cache: 'reload',
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-cache',
      },
    });
    if (!res.ok) {
      // Server-Side Overpass-Fail → sauberer 503 + Body mit Hinweis.
      const text = await res.text().catch(() => '');
      throw new Error(
        res.status === 503
          ? `Server can't reach OpenStreetMap right now. Try again in a few minutes.`
          : `Server returned HTTP ${res.status}: ${text.slice(0, 120)}`,
      );
    }
    const data = (await res.json()) as PoiBlobResponse;
    if (!data || !Array.isArray(data.pois) || typeof data.cachedAt !== 'string') {
      throw new Error('Server returned an invalid POI blob.');
    }
    return data;
  } finally {
    clearTimeout(t);
  }
}

/**
 * Speichert einen POI-Blob als SINGLE IDB-Row in poiBlobs. Keine Multi-
 * Put-Transaktion mehr → iOS Safari kommt nicht ins Schleudern.
 */
async function storePoiBlob(
  category: PoiCategoryId,
  blob: PoiBlobResponse,
): Promise<void> {
  await withDb(async (db) => {
    const row: PoiBlobRow = {
      category,
      count: blob.pois.length,
      cachedAt: blob.cachedAt,
      pois: blob.pois,
      v: POI_BLOB_VERSION,
    };
    await db.put('poiBlobs', row);
  });
}

/** Liest den Blob einer Kategorie aus IDB, oder null wenn nicht gecached. */
async function loadPoiBlob(
  category: PoiCategoryId,
): Promise<PoiBlobRow | null> {
  return withDb(async (db) => {
    const row = (await db.get('poiBlobs', category)) as PoiBlobRow | undefined;
    return row ?? null;
  });
}

/**
 * Lädt gecachte POIs einer Kategorie aus IDB.
 * Falls kein Cache: leeres Array (Caller soll dann Download anbieten).
 */
export async function loadCachedPois(category: PoiCategoryId): Promise<POI[]> {
  const row = await loadPoiBlob(category);
  return row?.pois ?? [];
}

export interface GeocodeResult {
  id: string;
  name: string;
  address: string;
  lat: number;
  lon: number;
}

/** Ortssuche (Geocode) via Backend/Google. Für den Test-Standort im Profil. */
export async function geocodePlace(q: string): Promise<GeocodeResult[]> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), POI_FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(`/api/pois/geocode?q=${encodeURIComponent(q)}`, {
      signal: ctrl.signal,
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(
        res.status === 503
          ? 'Geocoding nicht verfügbar (GOOGLE_PLACES_API_KEY fehlt auf dem Server).'
          : `Geocode HTTP ${res.status}: ${txt.slice(0, 120)}`,
      );
    }
    const data = (await res.json()) as { results?: GeocodeResult[] };
    return Array.isArray(data.results) ? data.results : [];
  } finally {
    clearTimeout(t);
  }
}

// ───────────────────────── Google-POI-Cache (privat) ─────────────────────
// Persistenter, area-gerasterter Cache für Live-Google-Ergebnisse. Sinn: einmal
// für ein Gebiet geladen → erneutes Anschauen nutzt den Cache statt eines neuen
// (kostenpflichtigen) Google-Calls, und die POIs sind auch offline da, wo man
// schon war. NUR für den Privatgebrauch gedacht — für einen App-Store-Release
// müsste das wegen Google-ToS raus.
//
// Raster: ~0.05° (~5,5 km) Zellen. Gespeichert wird im settings-Store unter
// `gpoicache:<category>` → { "<cell>": { pois, cachedAt } }. Kein Schema-
// Upgrade nötig (settings ist ein generischer KV-Store).
const GOOGLE_CACHE_CELL_DEG = 0.05;
// Cache-Version: hochzählen, wenn sich die Such-Logik ändert, damit alte
// Zellen verworfen werden. v2 = Google rankt nach DISTANCE (vorher Prominenz).
// v3 = Öffnungszeiten (open_now/opening_hours) werden mitgeholt.
const GOOGLE_CACHE_VERSION = 3;
// 60 Tage: lange genug, dass vor dem Rennen gescoutete Gebiete bis ins Ziel
// (Barcelona, Mitte Juli) ohne neuen Call gecacht bleiben. Bäcker/Supermärkte
// ändern sich selten.
const GOOGLE_CACHE_MAX_AGE_MS = 60 * 24 * 60 * 60 * 1000; // 60 Tage
const googleCacheKey = (cat: PoiCategoryId) => `gpoicache:${cat}`;
const googleCellKey = (lat: number, lon: number): string =>
  `${Math.round(lat / GOOGLE_CACHE_CELL_DEG)}_${Math.round(lon / GOOGLE_CACHE_CELL_DEG)}`;

type GoogleCellCache = Record<string, { pois: POI[]; cachedAt: number; v?: number }>;

/** Gecachte Google-POIs für die Zelle um (lat,lon), oder null (Miss/stale). */
export async function loadGooglePoiCache(
  category: PoiCategoryId,
  lat: number,
  lon: number,
): Promise<POI[] | null> {
  try {
    return await withDb(async (db) => {
      const blob = (await db.get('settings', googleCacheKey(category))) as
        | GoogleCellCache
        | undefined;
      const entry = blob?.[googleCellKey(lat, lon)];
      if (!entry) return null;
      if (entry.v !== GOOGLE_CACHE_VERSION) return null; // alte Logik → verwerfen
      if (Date.now() - entry.cachedAt > GOOGLE_CACHE_MAX_AGE_MS) return null;
      return entry.pois;
    });
  } catch {
    return null;
  }
}

/** Google-POIs für die Zelle um (lat,lon) persistieren (best effort). */
export async function saveGooglePoiCache(
  category: PoiCategoryId,
  lat: number,
  lon: number,
  pois: POI[],
): Promise<void> {
  try {
    await withDb(async (db) => {
      const blob =
        ((await db.get('settings', googleCacheKey(category))) as GoogleCellCache | undefined) ?? {};
      blob[googleCellKey(lat, lon)] = { pois, cachedAt: Date.now(), v: GOOGLE_CACHE_VERSION };
      await db.put('settings', blob, googleCacheKey(category));
    });
  } catch {
    // Cache ist Best-Effort — Fehler ignorieren.
  }
}

/**
 * Cache-first Google-POIs: liefert gecachte Treffer der Zelle (auch offline),
 * sonst — wenn `online` — ein frischer Live-Call (der persistiert wird). Wirft,
 * wenn weder Cache noch erfolgreicher Call → Caller fällt auf OSM zurück.
 */
export async function getGooglePois(
  category: PoiCategoryId,
  lat: number,
  lon: number,
  online: boolean,
  radiusM = 15000,
): Promise<POI[]> {
  const cached = await loadGooglePoiCache(category, lat, lon);
  if (cached && cached.length) return cached;
  if (!online) throw new Error('offline_no_cache');
  const live = await fetchLivePois(category, lat, lon, radiusM);
  if (live.length) void saveGooglePoiCache(category, lat, lon, live);
  return live;
}

/**
 * Live-Google-Places um eine Position. Nur für GOOGLE_LIVE_CATEGORIES sinnvoll;
 * setzt Online + gesetzten Server-Key voraus. Wirft bei Fehler (Caller fängt
 * und fällt auf loadCachedPois/OSM zurück). Wird NICHT persistiert — bewusst
 * On-Demand (Kosten + Google-ToS).
 */
export async function fetchLivePois(
  category: PoiCategoryId,
  lat: number,
  lon: number,
  radiusM = 15000,
): Promise<POI[]> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), POI_FETCH_TIMEOUT_MS);
  try {
    const url =
      `/api/pois/live?category=${encodeURIComponent(category)}` +
      `&lat=${lat}&lon=${lon}&radius=${Math.round(radiusM)}&_=${Date.now()}`;
    const res = await fetch(url, {
      signal: ctrl.signal,
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`live HTTP ${res.status}: ${text.slice(0, 120)}`);
    }
    const data = (await res.json()) as PoiBlobResponse;
    if (!data || !Array.isArray(data.pois)) {
      throw new Error('Live-POI-Antwort ungültig.');
    }
    return data.pois;
  } finally {
    clearTimeout(t);
  }
}

/**
 * Liefert POIs einer Kategorie: Cache first, Fetch wenn leer/stale.
 *
 * Verhalten:
 *   - Cache vorhanden + frisch (<14d): sofort zurück, kein Netz.
 *   - Cache vorhanden + stale: Cache zurück + silent background-refresh.
 *   - Cache leer: Fetch vom Backend (inkl. evtl. serverseitigem Overpass-
 *     Refresh, bis zu ~20s). Wenn offline: leere Liste + error.
 */
export async function loadPoisWithRefresh(
  category: PoiCategoryId,
): Promise<{ pois: POI[]; fromCache: boolean; error?: string }> {
  const cached = await loadPoiBlob(category);
  // Veraltete Daten-Version (neuer Korridor) zählt wie "nicht frisch" → wir
  // laden im Vordergrund neu, statt 14 Tage auf den alten Blob zu warten.
  const versionOk = cached?.v === POI_BLOB_VERSION;
  // …und dasselbe, wenn der Server inzwischen einen anderen Stand hat
  // (Self-Service-Korridor gebaut oder von der Retention abgeräumt).
  const serverMoved = versionOk
    ? await serverBlobDiffers(category, cached?.cachedAt)
    : false;
  const fresh =
    cached != null &&
    versionOk &&
    !serverMoved &&
    Date.now() - new Date(cached.cachedAt).getTime() < CACHE_MAX_AGE_MS;

  if (cached && fresh) {
    return { pois: cached.pois, fromCache: true };
  }

  if (cached && versionOk && !serverMoved && !fresh) {
    // Stale (aber richtige Version) — sofort Cache zurück, im Hintergrund neu.
    refreshInBackground(category);
    return { pois: cached.pois, fromCache: true };
  }

  // Cache leer, veraltete Version ODER Server-Stand gewandert → Vordergrund-
  // Fetch zwingend, damit ein frisch gebauter Korridor sofort POIs hat.
  try {
    const blob = await fetchPoiBlobFromBackend(category);
    await storePoiBlob(category, blob);
    return { pois: blob.pois, fromCache: false };
  } catch (err) {
    // Offline o.ä.: lieber den alten Cache zeigen als gar nichts.
    if (cached) return { pois: cached.pois, fromCache: true };
    return {
      pois: [],
      fromCache: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

function refreshInBackground(category: PoiCategoryId): void {
  void (async () => {
    try {
      const blob = await fetchPoiBlobFromBackend(category);
      await storePoiBlob(category, blob);
    } catch {
      // Silent — stale Cache bleibt erhalten, nächster Mount versucht's erneut.
    }
  })();
}

/**
 * Hard-reset: Cache einer Kategorie löschen. Da wir jetzt nur eine Row
 * pro Kategorie haben, ist das ein einzelner delete().
 */
export async function clearPoiCache(category: PoiCategoryId): Promise<void> {
  await withDb(async (db) => {
    await db.delete('poiBlobs', category);
  });
}

/**
 * Meta-Info über den Cache — Anzahl Items + ältester Cached-At.
 * (Im Blob-Modell sind Count + CachedAt direkt in der Row — kein
 * Iterieren nötig.)
 */
export async function getPoiCacheInfo(
  category: PoiCategoryId,
): Promise<{ count: number; oldestCachedAt: string | null }> {
  const row = await loadPoiBlob(category);
  if (!row) return { count: 0, oldestCachedAt: null };
  return { count: row.count, oldestCachedAt: row.cachedAt };
}

/**
 * Alle POI-Kategorien prefetchen, damit sie offline verfügbar sind — auch
 * wenn der User sie nicht explizit aktiviert hat.
 *
 * Verhalten:
 *   - Kategorie hat CACHE → nichts tun.
 *   - Kategorie ist LEER + online → fetchen + cachen.
 *   - Kategorie ist LEER + offline → still scheitern, nächster Mount-Retry.
 *
 * Sequentiell (nicht parallel), damit wir den Server nicht mit N simultanen
 * Requests treffen — bei nur einer Kategorie heute egal, zukunftsfest.
 */
export async function ensurePoisAvailableOffline(): Promise<void> {
  const ids = Object.keys(POI_CATEGORIES) as PoiCategoryId[];
  for (const catId of ids) {
    try {
      const existing = await loadPoiBlob(catId);
      // Richtige Daten-Version UND gleicher Server-Stand → nichts tun.
      // Alt-Version oder gewanderter Server-Stand (neuer/abgeräumter
      // Korridor) → neu holen, damit die Strecke offline abgedeckt ist.
      if (
        existing &&
        existing.v === POI_BLOB_VERSION &&
        !(await serverBlobDiffers(catId, existing.cachedAt))
      ) {
        continue;
      }
      const blob = await fetchPoiBlobFromBackend(catId);
      await storePoiBlob(catId, blob);
    } catch {
      // Silent — offline/Server-Down ist ok, nächster Mount versucht's erneut.
    }
  }
}

/**
 * Explizites "Download now" — holt den aktuellen Blob vom Backend und
 * schreibt ihn als EINE IDB-Row. Der Server entscheidet intern ob seine
 * 14-Tage-TTL einen neuen Overpass-Call nötig macht. User kriegt damit:
 *   - Server-Cache frisch (<14d) → instant Response, keine Overpass-Last
 *   - Server-Cache stale oder leer → Server triggert synchron Overpass,
 *     dann Response mit frischen Daten
 *
 * Wir triggern hier bewusst KEINEN expliziten Force-Refresh per POST — das
 * würde bei rate-limited-Overpass unnötig failen, obwohl der Server
 * bereits gut gecachte Daten hat. Der Admin-Force-Refresh-Endpoint
 * (POST /api/pois/:category/refresh) existiert fürs Backend-Ops-Tooling,
 * nicht fürs UI.
 *
 * Wirft bei Fehler (Caller zeigt inline-error, siehe OfflinePage.downloadPoi).
 */
export async function forceRefreshPoiCategory(
  category: PoiCategoryId,
): Promise<{ count: number }> {
  const blob = await fetchPoiBlobFromBackend(category);
  await storePoiBlob(category, blob);
  return { count: blob.pois.length };
}
